/** @type {Window & { Shopify?: any, webxpClientInitialConfig?: any }} */
const win = /** @type {any} */ (window);

import { Component } from '@theme/component';
import { publishToShopify, sha256Hex } from '@theme/apc-datalayer';

/** The Bloomreach event a sign-up records. */
const OPT_IN_EVENT = 'double_opt_in';

/** Bloomreach's unauthenticated, stream-scoped ingest endpoint. */
const BATCH_PATH = '/track/u/v1/batch';

/**
 * @param {string} name
 * @returns {string | null}
 */
function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Bloomreach newsletter sign-up component.
 * Used by the email signup block and the newsletter section.
 *
 * The sign-up must land whatever the visitor's cookie choice, so the form is a
 * real `{% form 'customer' %}` and the opt-in event is posted straight to
 * Bloomreach. Success is rendered server-side from `form.posted_successfully?`.
 *
 * @typedef {object} BloomreachNewsletterRefs
 * @property {HTMLFormElement} form - The customer form.
 * @property {HTMLInputElement} emailInput - The email input.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} [errorMessage] - The client-side validation message.
 * @property {HTMLInputElement} [disclaimerCheckbox] - The disclaimer checkbox.
 *
 * @extends Component<BloomreachNewsletterRefs>
 */
class BloomreachNewsletter extends Component {
  requiredRefs = ['form', 'emailInput', 'submitButton'];

  /** @type {Record<string, any>} */
  #config = {};

  connectedCallback() {
    super.connectedCallback();
    const configScript = this.querySelector('script[type="application/json"]');
    this.#config = configScript ? JSON.parse(configScript.textContent) : {};
    this.#prefillEmail();
  }

  get customerEmail() {
    return this.#config.customerEmail || null;
  }

  get source() {
    return this.#config.source || 'newsletter';
  }

  get consentCategory() {
    return this.#config.consentCategory || 'newsletter';
  }

  /** BCP 47 locale of the storefront the sign-up came from, e.g. `de-AT`. */
  get customerLocale() {
    if (this.#config.customerLocale) return this.#config.customerLocale;

    const { locale, country } = win.Shopify ?? {};
    return locale && country ? `${locale}-${country}` : '';
  }

  #prefillEmail() {
    if (this.customerEmail) {
      this.refs.emailInput.value = this.customerEmail;
    }
  }

  /**
   * Records the opt-in, then lets the browser submit the form.
   * Never cancel a valid submit: Shopify's hCaptcha only issues its token
   * during the browser's own submit, and a `fetch` POST is rejected with
   * `400 Missing CAPTCHA token`.
   * @param {SubmitEvent} [event]
   */
  handleSubmit = (event) => {
    const { emailInput } = this.refs;
    const email = emailInput.value.trim();

    this.#hideError();

    if (!email || !this.#isValidEmail(email)) {
      event?.preventDefault();
      this.#showError(this.#config.errors?.email || 'Please enter a valid email address');
      emailInput.focus();
      return;
    }

    // Validate disclaimer checkbox if present
    if (this.refs.disclaimerCheckbox && !this.refs.disclaimerCheckbox.checked) {
      event?.preventDefault();
      this.#showError(this.#config.errors?.disclaimer || 'Please accept the disclaimer to continue');
      return;
    }

    // No loading state: hCaptcha can route through a challenge page first, so a
    // spinner would sit there disabled until the visitor deals with it.
    // Fire-and-forget — neither may delay or cancel the submit.
    this.#sendOptInEvent(email);
    this.#trackNewsletterSubscribe(email);
  };

  /**
   * Sends the opt-in event, bypassing `exponea.track()`. The app embed leaves
   * `window.exponea` a command-queue stub until analytics *and* marketing *and*
   * sale-of-data consent are granted, so `track()` silently sends nothing — and
   * the stub is always present, so no feature check can detect it.
   * Uses `keepalive`, since the page is about to navigate.
   * @param {string} email
   */
  async #sendOptInEvent(email) {
    const endpoint = this.#endpoint();

    if (!endpoint) {
      console.warn('[APC] Bloomreach newsletter: no tracking config, opt-in event not sent');
      return;
    }

    const { url, streamId } = endpoint;
    const { integrationId, integrationType } = this.#config;

    const payload = {
      commands: [
        {
          name: 'customers/events',
          data: {
            company_id: streamId,
            customer_ids: this.#customerIds(email),
            type: OPT_IN_EVENT,
            properties: {
              action: 'new',
              email,
              source: this.source,
              customer_locale: this.customerLocale,
              consent_list: [
                {
                  action: 'accept',
                  category: this.consentCategory,
                  valid_until: 'unlimited',
                },
              ],
              location: window.location.href,
              page_title: document.title,
              referrer: document.referrer,
              ...(integrationId && { integration_id: integrationId }),
              ...(integrationType && { integration_type: integrationType }),
            },
          },
        },
      ],
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        // Matches the app embed's own calls; also avoids a CORS preflight.
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
        keepalive: true,
      });

      if (!response.ok) throw new Error(`Bloomreach responded with ${response.status}`);
    } catch (error) {
      console.error('[APC] Bloomreach newsletter: could not deliver the opt-in event', error);
    }
  }

  /**
   * Target and stream id, read from the app embed at runtime. Which global holds
   * them depends on how far the SDK got — the snippet parks its config on
   * `webxpClient` and the loaded SDK moves it — so try every known location.
   * @returns {{ url: string, streamId: string } | null}
   */
  #endpoint() {
    const candidates = [win.webxpClientInitialConfig, win.webxpClient, ...this.#queuedSdkConfigs(), this.#config];

    for (const candidate of candidates) {
      const target = candidate?.path || candidate?.target;
      const streamId = candidate?.streamId || candidate?.stream_id;

      if (target && streamId) {
        return {
          url: `${target}${BATCH_PATH}?stream_id=${encodeURIComponent(streamId)}`,
          streamId,
        };
      }
    }

    return null;
  }

  /**
   * Config objects queued on the SDK stub, which records calls as `[name, arguments]`.
   * @returns {any[]}
   */
  #queuedSdkConfigs() {
    const queue = win.exponea?._;
    if (!Array.isArray(queue)) return [];

    return queue.flatMap(([, args]) => (args ? Array.from(args) : []));
  }

  /**
   * `email_id` is this project's hard ID — Bloomreach rejects `registered`.
   * The soft IDs are absent for a visitor who declined cookies.
   * @param {string} email
   * @returns {Record<string, string>}
   */
  #customerIds(email) {
    /** @type {Record<string, string>} */
    const ids = { email_id: email };

    const cookieId = readCookie('__exponea_etc__');
    if (cookieId) ids.cookie = cookieId;

    // The `cart` cookie carries a `?key=` suffix.
    const cartId = readCookie('cart')?.split('?')[0];
    if (cartId) ids.cart_id = cartId;

    return ids;
  }

  /**
   * Pushes a `newsletter_subscribe` event to the dataLayer (ketrk/GTM).
   * @param {string} email
   */
  async #trackNewsletterSubscribe(email) {
    publishToShopify('newsletter_subscribe', {
      email,
      email_sha256: await sha256Hex(email),
    });
  }

  /**
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * The block wraps its error in an icon plus an empty span, so write into the
   * span rather than the container, which would delete the icon.
   * @returns {HTMLElement | undefined}
   */
  get #errorText() {
    const { errorMessage } = this.refs;
    return errorMessage?.querySelector('span') ?? errorMessage;
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;

    const target = this.#errorText;
    if (target) target.textContent = message;
    errorMessage.hidden = false;
  }

  #hideError() {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;

    errorMessage.hidden = true;

    const target = this.#errorText;
    if (target) target.textContent = '';
  }
}

if (!customElements.get('apc-bloomreach-newsletter')) {
  customElements.define('apc-bloomreach-newsletter', BloomreachNewsletter);
}

export { BloomreachNewsletter };
